import { Component, QueryAll } from '@atomify/core';
import { GTMPush } from '@utilities/gtm';
import { isMobile, useMedia } from '@utilities/media-query';

interface Sub {
    trigger: HTMLElement;
    element: HTMLElement;
    inner: HTMLElement;
    height?: number;
    isOpen: boolean;
}

const SUB_DROPDOWN_ACTIVE_CLASS = 'navigation__item--dropdown-active';

@Component({
    tag: 'bpd-navigation',
})
export class BPDNavigation extends HTMLElement {
    @QueryAll('[js-hook-navigation-sub-trigger]') subTriggers: HTMLElement[];
    @QueryAll('[js-hook-navigation-link-item]') links: HTMLElement[];

    subs: Sub[] = [];

    componentDidRender() {
        this.subs = this.getSubs();

        useMedia(({ tabletLandscape, tabletPortrait }) => {
            if (tabletLandscape || tabletPortrait) {
                this.setSubsHeight(this.subs);
                this.subs.forEach((sub) => this.closeSub(sub));
            }
        });

        this.bindSubMenuTriggerEvents();
        this.bindGTMEvents();
    }

    bindSubMenuTriggerEvents() {
        this.subs.forEach((sub) => {
            sub.trigger.addEventListener('mouseenter', () => this.handleMouseenter(sub));
            sub.trigger.addEventListener('mouseleave', () => this.handleMouseleave(sub));
            sub.trigger.addEventListener('click', (e) => this.handleTriggerClick(e, sub));
        });
    }

    handleMouseenter(sub: Sub) {
        if (!isMobile()) this.openSub(sub);
    }

    handleMouseleave(sub: Sub) {
        if (!isMobile()) this.closeSub(sub);
    }

    handleTriggerClick(_: Event, sub: Sub) {
        if (isMobile()) {
            this.closeOtherSubs(sub);
            sub.isOpen ? this.closeSub(sub) : this.openSub(sub);
        }
    }

    openSub(sub: Sub) {
        if (sub.element) {
            sub.element.style.maxHeight = `${sub.height}px`;
            sub.isOpen = true;
        }
        sub.trigger.classList.add(SUB_DROPDOWN_ACTIVE_CLASS);
    }

    closeSub(sub: Sub) {
        if (sub.element) {
            sub.element.style.maxHeight = `0px`;
            sub.isOpen = false;
        }
        sub.trigger.classList.remove(SUB_DROPDOWN_ACTIVE_CLASS);
    }

    closeOtherSubs(sub: Sub) {
        this.subs.forEach((otherSub) => {
            if (otherSub !== sub) this.closeSub(otherSub);
        });
    }

    getSubs() {
        const subs = [...this.subTriggers].map((trigger) => {
            const sub: Sub = {
                trigger,
                element: trigger.querySelector('[js-hook-navigation-sub]') as HTMLElement,
                inner: trigger.querySelector('[js-hook-navigation-sub-inner]') as HTMLElement,
                isOpen: false,
            };

            return sub;
        });

        this.setSubsHeight(subs);

        return subs;
    }

    setSubsHeight(subs: Sub[]) {
        subs.forEach((sub) => {
            const innerHeight = sub.inner.getBoundingClientRect().height;
            const innerStyles = window.getComputedStyle(sub.inner);
            const innerMargin =
                parseInt(innerStyles.marginTop) + parseInt(innerStyles.marginBottom);

            sub.height = innerHeight + innerMargin;
        });
    }

    bindGTMEvents() {
        [...this.links].forEach((item) => {
            const menuLink = item.querySelector<HTMLElement>('[js-hook-navigation-top-link]');

            menuLink?.addEventListener('click', () =>
                GTMPush({
                    event: 'menuUsed',
                    menuCategory: menuLink.innerText,
                    menuSubCategory: '',
                }),
            );
        });

        this.subs.forEach(({ trigger }) => {
            const subLinks = trigger.querySelectorAll<HTMLElement>('[js-hook-navigation-sub-link]');
            const menuTitle = trigger.querySelector<HTMLSpanElement>('span')?.innerText;

            subLinks.forEach((link) => {
                link.addEventListener('click', () =>
                    GTMPush({
                        event: 'menuUsed',
                        menuCategory: menuTitle,
                        menuSubCategory: link.innerText,
                    }),
                );
            });
        });
    }
}
